import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiWorker } from '../api/worker';
import { DirectoryMember, DirectoryFilters, MemberRelationship, InvitationResponse } from '../types';
import toast from 'react-hot-toast';

export const useDirectory = (nodeId: string, filters: DirectoryFilters = {}) => {
  return useQuery<{ members: DirectoryMember[]; nodeCount: number; relationships?: MemberRelationship[] }>({
    queryKey: ['directory', nodeId, filters],
    queryFn: () => 
      apiWorker.get(`/api/nodes/${nodeId}/members/directory`, {
        params: {
          page: 0,
          size: 50,
          ...(filters.name ? { name: filters.name } : {}),
          ...(filters.role && filters.role.length > 0 ? { role: filters.role.join(',') } : {}),
          ...(filters.skill ? { skill: filters.skill } : {}),
          ...(filters.attribute ? { attribute: filters.attribute } : {}),
          ...(filters.includeDescendants ? { includeDescendants: true } : {}),
        }
      }).then(r => {
        const sanitizeMembers = (list: any[]): DirectoryMember[] => {
          return (Array.isArray(list) ? list : []).map(m => ({
            ...m,
            skills: Array.isArray(m?.skills) ? m.skills : [],
            metadata: m?.metadata && typeof m.metadata === 'object' ? m.metadata : {}
          }));
        };

        if (r.data && Array.isArray(r.data.members)) {
          return {
            members: sanitizeMembers(r.data.members),
            nodeCount: typeof r.data.nodeCount === 'number' ? r.data.nodeCount : r.data.members.length,
            relationships: Array.isArray(r.data.relationships) ? r.data.relationships : []
          };
        }
        const content = r.data && Array.isArray(r.data.content) ? r.data.content : (Array.isArray(r.data) ? r.data : []);
        return {
          members: sanitizeMembers(content),
          nodeCount: r.data?.totalElements ?? content.length,
          relationships: Array.isArray(r.data?.relationships) ? r.data.relationships : []
        };
      }),
    enabled: !!nodeId,
  });
};

export const useMemberProfile = (nodeId: string, memberId: string) => {
  return useQuery<DirectoryMember & { relationships: MemberRelationship[] }>({
    queryKey: ['member-profile', nodeId, memberId],
    queryFn: () => apiWorker.get(`/api/nodes/${nodeId}/members/${memberId}`).then(r => {
      const data = (r.data && Array.isArray(r.data.content)) ? r.data.content[0] : r.data;
      if (!data) return data;
      return {
        ...data,
        skills: Array.isArray(data.skills) ? data.skills : [],
        metadata: data.metadata && typeof data.metadata === 'object' ? data.metadata : {}
      };
    }),
    enabled: !!nodeId && !!memberId,
  });
};

export const useUpdateMember = (nodeId: string, memberId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<DirectoryMember>) => 
      apiWorker.patch(`/api/nodes/${nodeId}/members/${memberId}`, data).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['member-profile', nodeId, memberId] });
      queryClient.invalidateQueries({ queryKey: ['directory', nodeId] });
      toast.success('Member profile updated');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to update member';
      toast.error(message);
    },
  });
};

export const useUpdateMemberRole = (nodeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) => 
      apiWorker.patch(`/api/nodes/${nodeId}/members/${userId}/role`, { role }).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['directory', nodeId] });
      toast.success('Role updated successfully');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to update role';
      toast.error(message);
    },
  });
};

export const useInviteMember = (nodeId: string) => {
  const queryClient = useQueryClient();

  return useMutation<InvitationResponse, any, { email: string; role: string }>({
    mutationFn: (data: { email: string; role: string }) => 
      apiWorker.post(`/api/nodes/${nodeId}/members/invite`, data).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    onSuccess: () => {
      // Invalidate the directory and pending invitations so the new invitation shows up instantly
      queryClient.invalidateQueries({ queryKey: ['directory', nodeId] });
      queryClient.invalidateQueries({ queryKey: ['node-pending-invitations', nodeId] });
      toast.success('Invitation sent successfully');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to send invitation';
      toast.error(message);
    }
  });
};

export const useNodePendingInvitations = (nodeId: string) => {
  return useQuery<{ invitations: InvitationResponse[]; totalElements: number }>({
    queryKey: ['node-pending-invitations', nodeId],
    queryFn: () => 
      apiWorker.get(`/api/invitations/${nodeId}/pending`, {
        params: { page: 0, size: 100 }
      }).then(r => {
        const content = r.data && Array.isArray(r.data.content) ? r.data.content : (Array.isArray(r.data) ? r.data : []);
        return {
          invitations: content,
          totalElements: r.data?.totalElements ?? content.length
        };
      }),
    enabled: !!nodeId,
  });
};

export const useRevokeInvitation = (nodeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invitationId: string) => 
      apiWorker.post(`/api/invitations/${invitationId}/revoke`).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['node-pending-invitations', nodeId] });
      queryClient.invalidateQueries({ queryKey: ['directory', nodeId] });
      toast.success('Invitation revoked successfully');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to revoke invitation';
      toast.error(message);
    }
  });
};

export const useRemoveMember = (nodeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (memberId: string) => 
      apiWorker.delete(`/api/nodes/${nodeId}/members/${memberId}`).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['directory', nodeId] });
      queryClient.invalidateQueries({ queryKey: ['leadership', nodeId] });
      toast.success('Member removed successfully');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to remove member';
      toast.error(message);
    }
  });
};

